import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '@/store/authStore';
import { isFirebaseConfigured, signInWithGoogle } from '@/services/firebase';
import Logo from '@/components/ui/Logo';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import styles from './Login.module.css';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const socialLogin = useAuthStore((s) => s.socialLogin);
  const logout = useAuthStore((s) => s.logout);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Sessão de aluno que caiu aqui (guard de rota): desloga e explica.
  useEffect(() => {
    const { isAuthenticated, user } = useAuthStore.getState();
    if (isAuthenticated && user?.role === 'student') {
      logout();
      setError(t('auth.studentAreaInApp'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O web é a área do personal/admin; conta de aluno acessa pelo app.
  const goHome = (user) => {
    if (user.role === 'student') {
      logout();
      setError(t('auth.studentAreaInApp'));
      return;
    }
    navigate(user.role === 'admin' ? '/admin' : '/trainer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      goHome(await login(email, password));
    } catch (err) {
      setError(err.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      const idToken = await signInWithGoogle();
      goHome(await socialLogin(idToken));
    } catch (err) {
      setError(err.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.lang}>
        <LanguageSwitcher />
      </div>
      <form className={styles.card} onSubmit={handleSubmit}>
        <Logo size={40} />
        <p className={styles.tagline}>{t('auth.tagline')}</p>

        <Input
          id="email"
          type="email"
          label={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@email.com"
          required
        />
        <Input
          id="password"
          type="password"
          label={t('auth.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />

        <div className={styles.forgotRow}>
          <button type="button" className={styles.linkMuted} onClick={() => navigate('/forgot-password')}>
            {t('auth.forgotPassword')}
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <Button type="submit" fullWidth loading={loading}>
          {t('auth.signIn')}
        </Button>

        {isFirebaseConfigured && (
          <Button type="button" variant="outline" fullWidth onClick={handleGoogle} disabled={loading}>
            {t('auth.google')}
          </Button>
        )}

        <p className={styles.footer}>
          {t('auth.newHere')}{' '}
          <button type="button" className={styles.link} onClick={() => navigate('/register')}>
            {t('auth.createAccount')}
          </button>
        </p>
      </form>
    </div>
  );
}
